import _ from 'lodash';
import moment from 'moment';
import { type ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  fetchHolterEvents,
  applyGroupEventChange,
  formatEventStrip,
  updateEventChangeAndNew,
} from '@/entities/event';
import { KEY_CANCEL, KEY_RECOIL, typesPersistEventV2 } from '@/shared/config';
import { useUpdateDeepCompareEffect } from '@/shared/lib/customHooks';
import { formatHrData, type ChartHRHandle } from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectEventChanges,
  selectEventNews,
  selectGroupEventChanges,
  selectReloadHrChart,
  selectSelectedDateValue,
  selectUpdatedSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { BookmarkHrChartHandle, BookmarkHrChartProps } from './bookmarkHrChart';

/**
 * Feeds the bookmark tab's daily heart-rate chart, loading the selected day's holter events and turning the hourly ECG summaries into minutely series. Refetches and reformats on date, reload and unsaved-event changes, prefetches the neighbouring days' events on two-second timers, and exposes arrow-button navigation through the forwarded ref.
 *
 * @param props - Chart inputs: study and profile ids, the ECG data map and the timezone offset.
 * @param ref - Forwarded ref the parent uses to trigger arrow-button navigation.
 * @returns An object carrying the tab record key, the chart data, the updated selected strip and the chart component ref.
 */
const useBookmarkHrChart = (
  { studyId, profileId, timezoneOffset, ecgDataMap }: BookmarkHrChartProps,
  ref: ForwardedRef<BookmarkHrChartHandle>,
) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Flags whether the bookmark tab's heart-rate chart is still loading. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const dateList = useReportStore((s) => s.dateList);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags that the selected day has no usable heart-rate data on the bookmark tab. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setCombinedEventsAction = useReportStore((s) => s.actions.setCombinedEvents);
  /**
   * Stores the merged, change-applied holter events backing the bookmark heart-rate chart. Forwards the value to the keyed report-store action bound to the tab-2 record key.
   *
   * @param v - The new value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setCombinedEvents = (v: Parameters<typeof setCombinedEventsAction>[1]) =>
    setCombinedEventsAction(keyRecoil, v);
  const reloadHrChart = useReportStore(selectReloadHrChart(keyRecoil));
  const updatedSelectedStrip = useReportStore(selectUpdatedSelectedStrip(keyRecoil));
  const [dataChart, setDataChart] = useState<any>();

  const fetchInBackgroundTimeout = useRef(null);
  const chartHrRef = useRef<ChartHRHandle>(null);
  let isActiveQuery: boolean;

  /**
   * Warms the holter event cache for a neighbouring day so switching dates on the bookmark heart-rate chart is instant. Issues a fire-and-forget fetch of the persisted event types over the given window under the AI holter cancel key, logging any failure rather than surfacing it.
   *
   * @param startSearchDate - ISO timestamp opening the prefetch window.
   * @param stopSearchDate - ISO timestamp closing the prefetch window.
   * @returns A promise resolving once the background fetch has been issued.
   */
  const fetchHolterEventsBackgroundV2 = async (startSearchDate: any, stopSearchDate: any) => {
    try {
      const holterEventsPersistFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate,
        stopSearchDate,
      };
      fetchHolterEvents(holterEventsPersistFilter, 0, true, KEY_CANCEL.API_HOLTER_AI);
    } catch (error) {
      logError('Failed to fetch holter events in background: ', error);
    }
  };

  /**
   * Loads the persisted holter events for the day selected on the bookmark heart-rate chart and hands them to the chart formatter. Raises the loading flag unless the request returns within 100ms, sorts and formats the events, applies unsaved event changes, new events and group type changes, stores the combined list, then filters down to the persisted types before formatting the heart-rate data.
   *
   * @param needLoading - When true, raises the chart loading flag and suppresses re-zooming after the redraw.
   * @returns A promise resolving once the day's events have been fetched and the chart data rebuilt.
   */
  const fetchHolterEventsByDateV2 = async (needLoading = true) => {
    if (needLoading) {
      setIsLoadingHrChart(true);
    }
    let downloadedPersistEvents: any[] = [];
    try {
      const start = performance.now();
      const startSearchDate = selectedDateValue;
      const stopSearchDate = moment(selectedDateValue).add(1, 'days').toISOString();
      const holterEventsPersistFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate,
        stopSearchDate,
      };
      const promise = [fetchHolterEvents(holterEventsPersistFilter, 0, true, null, false)];
      const [resultPersist] = await Promise.all(promise);
      if (performance.now() - start < 100) {
        setIsLoadingHrChart(false);
      }
      if (resultPersist?.events) {
        downloadedPersistEvents = [...resultPersist.events];
      }
      if (isActiveQuery) {
        const mergeEvents = downloadedPersistEvents.sort(
          (a, b) => Date.parse(a.start) - Date.parse(b.start),
        );
        const dataFormat = formatEventStrip(mergeEvents);
        const holterEventsByDateUpdate = updateEventChangeAndNew(
          dataFormat,
          eventChanges,
          eventNews,
          startSearchDate,
          stopSearchDate,
          typesPersistEventV2,
          true,
        );
        applyGroupEventChange(holterEventsByDateUpdate, groupEventChanges);
        setCombinedEvents(holterEventsByDateUpdate);
        const holterEventsByDate = holterEventsByDateUpdate.filter(
          (x: any) =>
            typesPersistEventV2.includes(x.type) || typesPersistEventV2.includes(x.oldType),
        );
        formatHrData({
          holterEventsByDate,
          needLoading,
          ecgDataMap,
          timezoneOffset,
          selectedDateValue,
          setDataChart,
          setIsEmptyData,
          setIsLoadingHrChart,
        });
      }
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  useEffect(() => {
    // *: Change to other date
    isActiveQuery = true;
    if (selectedDateValue) {
      chartHrRef.current.resetCurrentSelect();
      fetchHolterEventsByDateV2();
    }
    return () => {
      isActiveQuery = false;
    };
  }, [selectedDateValue, reloadHrChart]);

  useEffect(() => {
    if (dateList.length) {
      const currentSelectedDateIndex = _.findIndex(
        dateList,
        (x) => x.toISOString() === selectedDateValue,
      );
      if (currentSelectedDateIndex > -1) {
        if (currentSelectedDateIndex === 0) {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex + 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
          }, 2000);
        } else if (currentSelectedDateIndex === dateList.length - 1) {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex - 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
          }, 2000);
        } else {
          const firstStartSearchDate = moment(dateList[currentSelectedDateIndex + 1]).toISOString();
          const firstStopSearchDate = moment(firstStartSearchDate).add(1, 'days').toISOString();
          const secondStartSearchDate = moment(
            dateList[currentSelectedDateIndex - 1],
          ).toISOString();
          const secondStopSearchDate = moment(secondStartSearchDate).add(1, 'days').toISOString();
          fetchInBackgroundTimeout.current = setTimeout(() => {
            fetchHolterEventsBackgroundV2(firstStartSearchDate, firstStopSearchDate);
            fetchInBackgroundTimeout.current = setTimeout(() => {
              fetchHolterEventsBackgroundV2(secondStartSearchDate, secondStopSearchDate);
            }, 2000);
          }, 2000);
        }
      }
    }
    return () => {
      clearTimeout(fetchInBackgroundTimeout.current);
      fetchInBackgroundTimeout.current = null;
    };
  }, [dateList, selectedDateValue]);

  // *: Fetch and re-draw events
  useUpdateDeepCompareEffect(
    () => {
      isActiveQuery = true;
      if (selectedDateValue) {
        fetchHolterEventsByDateV2(false);
      }
    },
    [eventChanges, eventNews, groupEventChanges],
    () => {
      isActiveQuery = false;
    },
  );

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext: boolean) => {
      if (chartHrRef.current) {
        chartHrRef.current.handleClickArrowButton(isNext);
      }
    },
  }));

  return {
    keyRecoil,
    dataChart,
    updatedSelectedStrip,
    chartHrRef,
  };
};

export default useBookmarkHrChart;
